import { PropType } from 'vue';
import plantsClient from '@/core/services/plants';
import { Options, Vue } from 'vue-class-component';
import {
  AddressingType,
  BuildingDTO,
  RiserDTO,
  EntrancePlantDetailDTO,
  PeInterfaceConfig,
  RiserInterfaceMode,
  RiserInterfaceDTO,
  FloorConfigDTO,
} from '@/../client-generator/generated-clients/plants';
import { SET_APP_LOADING } from '@/core/store/action-types';
import { mapState } from 'vuex';
import HelpPopupButton from '@/views/components/HelpPopupButton/HelpPopupButton.vue';
import NumberButtons from '@/views/components/NumberButtons/NumberButtons.vue';
import { toLabel } from '@/core/utils/EntrancePlant/denomination-helper';
import { noAppartmentOptions, RiserInterfaceModeCombo } from '@/core/utils/EntrancePlant/entrance-plant.utils';

interface ApartmentDistribution {
  interfaceId: string;
  count: number;
}

interface RiserFloorDistribution {
  id: string;
  index: number;
  name: string;
  apartmentDistribution: ApartmentDistribution[];
  linkedFloorId?: string;
}

@Options({
  name: 'DialogEditRiser',
  components: {
    HelpPopupButton,
    NumberButtons,
  },
  props: {
    building: {
      type: Object as PropType<BuildingDTO>,
      required: true,
    },
    isVisible: {
      type: Boolean,
      required: true,
    },
    riserToEdit: {
      type: Object as PropType<RiserDTO>,
      required: true,
    },
    isNewRiser: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState(['currentEntrancePlant']),
  },
  watch: {
    isVisible: function (newValue) {
      this.isDialogVisible = newValue;
      if (this.isDialogVisible) {
        this.invalidNameReason = '';
        this.isValidName = true;
        this.initRiser();
      }
    },
    'riser.name': function () {
      if (this.riser) {
        this.verifyValidityOfName();
      }
    },
    "interfaceComboMode": function(value: RiserInterfaceModeCombo) {
      this.updateInterfacesToConfigure(value);
    },
    "mainScsAddress": function() {
      this.updateInterfacesAddress();
    }
  },
  emits: ['hide-dialog', 'update-list'],
})
export default class DialogEditRiser extends Vue {
  currentEntrancePlant!: EntrancePlantDetailDTO;
  building!: BuildingDTO;
  riserToEdit!: RiserDTO;

  readonly MIN_SCS_ADDRESS_INTERFACE = 1;
  readonly MAX_SCS_ADDRESS_INTERFACE = 39;
  availableScsAddressInterface: number[] = [];
  riser: RiserDTO = new RiserDTO();
  currentRiserInterfaces: PeInterfaceConfig[] = [];
  unEditedRiserInterfaces: PeInterfaceConfig[] = [];
  initialFloorDistributionSnapshot = '';
  riserFloorDistribution: RiserFloorDistribution[] = [];
  floorCounterSeed = 0;
  RiserInterfaceModeCombo = RiserInterfaceModeCombo;
  interfaceComboMode: RiserInterfaceModeCombo = RiserInterfaceModeCombo.Mode5IndipendentRiser;
  initialInterfaceMode: RiserInterfaceModeCombo = RiserInterfaceModeCombo.Mode5IndipendentRiser;
  mainScsAddress: number = 0;
  numOfTotalApartments: number = 0;
  addrNotValid: boolean[] = [];
  invalidAddrReason: string[] = [];

  isVisible!: boolean;
  isDialogVisible = false;
  isNewRiser!: boolean;
  isBrfa = false;
  brfaStep = 0;

  // Naming validation props
  isValidName = true;
  invalidNameReason: string = '';

  get anyScsError(): boolean {
    return this.addrNotValid.some(error => error === true);
  }

  get floorsCount(): number {
    return this.riserFloorDistribution.length;
  }

  get floorDistributionHasError(): boolean {
    if (!this.isBrfa) return false;

    return this.currentRiserInterfaces.some((intf, index) => {
      const interfaceId = this.getInterfaceDistributionKey(intf, index);

      const expected = intf.numberOfApartments ?? 0;
      const assigned = this.riserFloorDistribution.reduce((sum, floor) => {
        const distribution = floor.apartmentDistribution.find(d => d.interfaceId === interfaceId);
        return sum + (distribution?.count ?? 0);
      }, 0);

      return assigned !== expected;
    });
  }

  get floorDistributionErrorText(): string[] {
    if (!this.isBrfa) return [];

    return this.currentRiserInterfaces.map((intf, index) => {
      const interfaceId = this.getInterfaceDistributionKey(intf, index);
      const modeLabel = this.getInterfaceDescription(intf.mode, index, this.currentRiserInterfaces.length) || this.$t('AppResourceEP.RiserInterface');
      const expected = intf.numberOfApartments ?? 0;
      const assigned = this.riserFloorDistribution.reduce((sum, floor) => {
        const distribution = floor.apartmentDistribution.find(d => d.interfaceId === interfaceId);
        return sum + (distribution?.count ?? 0);
      }, 0);

      return `${modeLabel}: ${assigned}/${expected}`;
    });
  }

  noAppartmentOptions(): { label: string, value: number }[] {
    if (this.isNewRiser) {
      return noAppartmentOptions(100, true);
    }
    const options = [];
    for (let i = 0; i <= this.numOfTotalApartments; i++) {
      options.push({ label: i.toString(), value: i });
    }
    return options;
  }

  mounted() {
    this.isDialogVisible = this.isVisible;
    this.isBrfa = this.currentEntrancePlant.addressingType === AddressingType.BuildingRiserFloorApartment;
  }

  initRiser() {
    this.brfaStep = 0;
    this.riser = new RiserDTO(this.riserToEdit);
    this.mainScsAddress = this.riser.interfaces![0].scsAddress ?? this.MIN_SCS_ADDRESS_INTERFACE;
    this.unEditedRiserInterfaces = this.riser.interfaces?.map(riserInterface => {
      return new PeInterfaceConfig({
        peBuildingConfigId: this.building.buildingId,
        peRiserConfigId: this.riser.riserId!,
        mode: riserInterface.mode!,
        numberOfApartments: this.getApNumbersFromInterface(riserInterface),
        id: riserInterface.riserInterfaceId!,
        scsAddress: riserInterface.scsAddress,
      })
    }) ?? [];

    this.currentRiserInterfaces = this.riser.interfaces?.map(riserInterface => {
      return new PeInterfaceConfig({
        peBuildingConfigId: this.building.buildingId,
        peRiserConfigId: this.riser.riserId!,
        mode: riserInterface.mode!,
        numberOfApartments: this.getApNumbersFromInterface(riserInterface),
        id: riserInterface.riserInterfaceId!,
        scsAddress: riserInterface.scsAddress,
      })
    }) ?? [];

    this.numOfTotalApartments = this.currentRiserInterfaces.reduce((acc, riserInterface) => acc + (riserInterface.numberOfApartments ?? 0), 0);

    this.availableScsAddressInterface = Array.from(
      { length: this.MAX_SCS_ADDRESS_INTERFACE },
      (_, i) => i + 1
    );

    switch (this.currentRiserInterfaces.length) {
      default:
      case 1:
        this.initialInterfaceMode = this.interfaceComboMode = RiserInterfaceModeCombo.Mode5IndipendentRiser;
        break;
      case 2:
        this.initialInterfaceMode = this.interfaceComboMode = RiserInterfaceModeCombo.Mode6ExtendedRiserAndOneMode7ExtendedRiserExpansion;
        break;
      case 3:
        this.initialInterfaceMode = this.interfaceComboMode = RiserInterfaceModeCombo.Mode6ExtendedRiserAndTwoMode7ExtendedRiserExpansion;
        break;
    }

    this.resetValidation();
    this.validateAddresses();
    this.initFloorDistribution();
    this.initialFloorDistributionSnapshot = this.serializeFloorDistribution();
  }

  handleHideDialog() {
    this.isDialogVisible = false;
  }

  doHideDialog() {
    this.$emit('hide-dialog');
  }

  get dialogTitle() {
    if (this.isBrfa && this.brfaStep === 1) {
      return this.$t('AppResourceEP.SetupWizardStepBuidingLinkApartemntsTitle');
    }
    return this.$t('AppResourceEP.EditRiser');
  }

  get buttonSubmitLabel() {
    if (this.isBrfa && this.brfaStep === 0) {
      return this.$t('AppResource.Continue');
    }
    return this.$t('AppResource.Save');
  }

  get secondaryButtonLabel() {
    if (this.isBrfa && this.brfaStep === 1) {
      return this.$t('AppResource.Back');
    }
    return this.$t('AppResource.Cancel');
  }

  get errorWithApartmentNumber(): boolean {
    if (this.isNewRiser) return false;
    return this.currentRiserInterfaces.some(intf => intf.numberOfApartments === undefined) ||
      this.currentRiserInterfaces.reduce((acc, intf) => acc + (intf.numberOfApartments ?? 0), 0) > this.numOfTotalApartments;
  }

  get mismatchedApartmentsErrorText() {
    const firstLine = this.$t('entrancePlant.riserApartments') + ': ' + this.numOfTotalApartments;
    const secondLine = this.$t('entrancePlant.assignedApartmets') + ': ' + this.currentRiserInterfaces.reduce((acc, intf) => acc + (intf.numberOfApartments ?? 0), 0);
    return [firstLine, secondLine];
  }

  get saveButtonDisabled() {
    const noFloorChanges = this.serializeFloorDistribution() === this.initialFloorDistributionSnapshot;
    const noChanges = this.riser.name === this.riserToEdit.name &&
      JSON.stringify(this.currentRiserInterfaces) === JSON.stringify(this.unEditedRiserInterfaces) &&
      (!this.isBrfa || noFloorChanges);

    const hasStepOneErrors = !this.addrNotValid.every(error => error === false) || !this.isValidName || !this.riser.name?.trim() || this.errorWithApartmentNumber;

    if (this.isBrfa && this.brfaStep === 0) {
      return hasStepOneErrors;
    }

    return hasStepOneErrors || noChanges || this.floorDistributionHasError;
  }

  verifyValidityOfName() {
    if (this.riser.name === undefined) return;

    const nameToCheck = this.riser.name.trim();
    const listToCheck = (this.building.risers || []).filter(
      (r) => r.riserId !== this.riserToEdit.riserId
    );

    const exist = listToCheck.some(
      (existing) =>
        existing.name?.toLowerCase().trim() === nameToCheck.toLowerCase().trim()
    );
    this.isValidName = !exist;
    this.invalidNameReason = this.isValidName
      ? ''
      : this.$t('general.alreadyExisting');
  }

  handleSecondaryButtonAction() {
    if (this.isBrfa && this.brfaStep === 1) {
      this.brfaStep = 0;
      return;
    }

    this.handleHideDialog();
  }

  async handleButtonSubmitAction() {
    if (this.isBrfa && this.brfaStep === 0) {
      this.brfaStep = 1;
      return;
    }

    this.isDialogVisible = false;
    await this.saveRiser();
    this.$emit('update-list');
  }

  async saveRiser() {
    if (this.currentEntrancePlant.desktopId === undefined) return;
    this.$store.dispatch(SET_APP_LOADING, true);
    try {
      await plantsClient.riserPUT(this.currentEntrancePlant.desktopId, this.riser);
      await plantsClient.riserinterfaces(this.currentEntrancePlant.desktopId, this.currentRiserInterfaces);

      if (this.isBrfa) {
        const floorDTOs = this.riserFloorDistribution.map((floor, index) => {
          const linkedInterfaces = floor.apartmentDistribution
            .map(d => {
              const intf = this.getInterfaceByDistributionKey(d.interfaceId);
              return new PeInterfaceConfig({
                id: intf?.id,
                peRiserConfigId: this.riser.riserId,
                peBuildingConfigId: this.building.buildingId,
                numberOfApartments: d.count,
                scsAddress: intf?.scsAddress,
                mode: intf?.mode,
              });
            });

          return new FloorConfigDTO({
            index: floor.index,
            name: floor.name,
            numberOfApartments: floor.apartmentDistribution.reduce((s, d) => s + d.count, 0),
            linkedInterfaces,
          });
        });

        await plantsClient.riserfloors(this.currentEntrancePlant.desktopId, floorDTOs);
      }

      this.$toast.add({
        severity: 'success',
        summary: this.$t('entrancePlant.editedRiser'),
        closable: false,
        life: 3000,
      });
    } catch (error) {
      console.log(error);
      setTimeout(() => {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          closable: false,
          life: 3000,
        });
      }, 1500);
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  getApNumbersFromInterface(riserInterface: RiserInterfaceDTO): number {
    if (this.isNewRiser) return 0;
    return this.building.apartments?.filter(ap => ap.riserInterfaceId === riserInterface.riserInterfaceId).length ?? 0;
  }

  private initFloorDistribution(): void {
    if (!this.isBrfa) {
      this.riserFloorDistribution = [];
      return;
    }

    const riserId = this.riser.riserId;
    const linkedFloors = (this.building.floors ?? [])
      .filter((floor) => floor.riserId === riserId)
      .sort((a, b) => (a.index ?? 0) - (b.index ?? 0));

    this.floorCounterSeed = linkedFloors.length;
    this.riserFloorDistribution = linkedFloors.map((floor, index) => {
      const floorId = floor.floorId ?? '';
      return {
        id: floorId || this.nextTempFloorId(),
        index: floor.index ?? index + 1,
        linkedFloorId: floor.floorId,
        name: floor.name || this.defaultFloorName(index + 1),
        apartmentDistribution: this.currentRiserInterfaces.map((intf, intfIndex) => {
          const interfaceId = this.getInterfaceDistributionKey(intf, intfIndex);
          const count = this.countApartmentsForFloorAndInterface(floor.floorId, intf.id);
          return {
            interfaceId,
            count,
          };
        }),
      };
    });

    if (this.riserFloorDistribution.length === 0 && this.isNewRiser) {
      this.addFloorDistribution();
    }

    this.syncFloorDistributionWithInterfaces(false);
  }

  private countApartmentsForFloorAndInterface(floorId: string | undefined, interfaceId: string | undefined): number {
    if (!floorId || !interfaceId) return 0;

    return this.building.apartments?.filter((apartment) => apartment.floorId === floorId && apartment.riserInterfaceId === interfaceId).length ?? 0;
  }

  addFloorDistribution(): void {
    if (!this.isBrfa || !this.isNewRiser) return;

    const nextIndex = this.riserFloorDistribution.length + 1;
    this.riserFloorDistribution.push({
      id: this.nextTempFloorId(),
      index: nextIndex,
      name: this.defaultFloorName(nextIndex),
      apartmentDistribution: this.currentRiserInterfaces
        .map((intf, index) => ({
          interfaceId: this.getInterfaceDistributionKey(intf, index),
          count: 0,
        })),
    });

    this.syncFloorDistributionWithInterfaces(true);
  }

  removeFloorDistribution(floorIndex: number): void {
    if (!this.isBrfa || !this.isNewRiser) return;
    if (floorIndex < 0 || floorIndex >= this.riserFloorDistribution.length) return;

    this.riserFloorDistribution.splice(floorIndex, 1);
    this.syncFloorDistributionWithInterfaces(true);
  }

  apartmentCountValues(interfaceId: string, floorId: string): { label: number; value: number; disabled: boolean }[] {
    const selectedInterface = this.getInterfaceByDistributionKey(interfaceId);
    const totalApartments = selectedInterface?.numberOfApartments ?? 0;
    const floorIndex = this.riserFloorDistribution.findIndex(floor => floor.id === floorId);

    if (floorIndex === -1) return [];

    let allocatedInOtherFloors = 0;
    this.riserFloorDistribution.forEach((floor, index) => {
      if (index === floorIndex) return;
      const distribution = floor.apartmentDistribution.find(d => d.interfaceId === interfaceId);
      allocatedInOtherFloors += distribution?.count ?? 0;
    });

    const maxAvailable = totalApartments - allocatedInOtherFloors;
    return [...Array(totalApartments + 1).keys()].map((value) => ({
      label: value,
      value,
      disabled: value > maxAvailable,
    }));
  }

  onDistributionChange(distribution: ApartmentDistribution, event: { originalEvent: any; value: number }): void {
    const isDisabled = (event.originalEvent?.currentTarget?.classList as DOMTokenList)?.contains('p-disabled');
    if (isDisabled) return;

    distribution.count = event.value;
  }

  private syncFloorDistributionWithInterfaces(rebalanceNewInterfaces: boolean): void {
    if (!this.isBrfa) return;

    const interfaceIds = this.currentRiserInterfaces
      .map((intf, index) => this.getInterfaceDistributionKey(intf, index));

    this.riserFloorDistribution.forEach((floor) => {
      floor.apartmentDistribution = floor.apartmentDistribution
        .filter(d => interfaceIds.includes(d.interfaceId));

      interfaceIds.forEach((interfaceId) => {
        const existing = floor.apartmentDistribution.find(d => d.interfaceId === interfaceId);
        if (!existing) {
          floor.apartmentDistribution.push({
            interfaceId,
            count: 0,
          });
        }
      });
    });

    if (rebalanceNewInterfaces) {
      interfaceIds.forEach((interfaceId) => this.rebalanceDistributionForInterface(interfaceId));
    }
  }

  private rebalanceDistributionForInterface(interfaceId: string): void {
    if (this.riserFloorDistribution.length === 0) return;

    const selectedInterface = this.getInterfaceByDistributionKey(interfaceId);
    const totalApartments = selectedInterface?.numberOfApartments ?? 0;
    const distribution = this.distributeElementsInGroups(totalApartments, this.riserFloorDistribution.length);

    this.riserFloorDistribution.forEach((floor, index) => {
      const floorDistribution = floor.apartmentDistribution.find(d => d.interfaceId === interfaceId);
      if (floorDistribution) {
        floorDistribution.count = distribution[index] ?? 0;
      }
    });
  }

  private distributeElementsInGroups(totalElements: number, groupsCount: number): number[] {
    if (groupsCount === 0) return [];

    const baseCount = Math.floor(totalElements / groupsCount);
    const remainder = totalElements % groupsCount;

    return [...Array(groupsCount).keys()].map(index => baseCount + (index < remainder ? 1 : 0));
  }

  private nextTempFloorId(): string {
    this.floorCounterSeed += 1;
    return `temp_floor_${this.floorCounterSeed}`;
  }

  private defaultFloorName(index: number): string {
    return `${this.$t('AppResourceEP.Floor')} ${toLabel(index, this.currentEntrancePlant.denominationFloors)}`;
  }

  private getInterfaceDistributionKey(intf: PeInterfaceConfig, index: number): string {
    return intf.id ?? `idx_${index}`;
  }

  private getInterfaceByDistributionKey(key: string): PeInterfaceConfig | undefined {
    const fromId = this.currentRiserInterfaces.find(intf => intf.id === key);
    if (fromId) return fromId;

    if (key.startsWith('idx_')) {
      const index = Number(key.replace('idx_', ''));
      if (!Number.isNaN(index)) {
        return this.currentRiserInterfaces[index];
      }
    }

    return undefined;
  }

  private serializeFloorDistribution(): string {
    if (!this.isBrfa) return '';

    return JSON.stringify(
      this.riserFloorDistribution.map((floor) => ({
        id: floor.id,
        linkedFloorId: floor.linkedFloorId,
        name: floor.name,
        apartmentDistribution: floor.apartmentDistribution
          .slice()
          .sort((a, b) => a.interfaceId.localeCompare(b.interfaceId)),
      }))
    );
  }

  updateInterfacesAddress(): void {
    this.currentRiserInterfaces.forEach((intf: PeInterfaceConfig, index: number) => {
      intf.scsAddress = Number(this.mainScsAddress) + index;
    });

    this.validateAddresses();
  }

  getInterfaceDescription(mode: RiserInterfaceMode | undefined, index: number, totInterfaces: number) {
    if (mode === RiserInterfaceMode.Mode5IndipendentRiser || 
        mode === RiserInterfaceMode.Mode6ExtendedRiser || 
        (mode === RiserInterfaceMode.Mode7ExtendedRiserExpansion && totInterfaces === 2)) {
      return `${this.$t("AppResourceEP.RiserInterface")} ${mode}`;
    }

    if (mode === RiserInterfaceMode.Mode7ExtendedRiserExpansion && totInterfaces > 2) {
      const orderValue = index === 1 ? this.$t("AppResourceEP.First") : this.$t("AppResourceEP.Second");
      return `${this.$t("AppResourceEP.RiserInterface")} ${mode} (${orderValue})`;
    }
  }

  private resetValidation(): void {
    this.addrNotValid = [];
    this.invalidAddrReason = [];
  }

  validateAddresses() {
    const interfaces = this.currentRiserInterfaces || [];

    // Get addresses used by current riser's interfaces
    const currentRiserAddresses = Object.values(interfaces)
      .map((intf: PeInterfaceConfig) => intf.scsAddress!)
      .filter((addr: number | undefined) => addr !== undefined && addr !== null)
      .map((addr: number) => Number(addr));

    // Get addresses used by other risers' interfaces only
    const otherRisersAddresses = this.currentEntrancePlant.buildings
      ?.flatMap(building => building.risers ?? [])
      .filter(riser => riser.riserId !== this.riser.riserId)
      .flatMap(riser => riser.interfaces ?? [])
      .map(intf => intf.scsAddress!)
      .filter((addr: number | undefined) => addr !== undefined && addr !== null)
      .map((addr: number) => Number(addr)) ?? [];

    interfaces.forEach((intf: PeInterfaceConfig, index: number) => {
      if (intf.scsAddress === undefined || intf.scsAddress === null) {
        this.addrNotValid[index] = true;
        this.invalidAddrReason[index] = '';
        return;
      }
      
      const value = Number(intf.scsAddress);
      const acceptedRange = value >= this.MIN_SCS_ADDRESS_INTERFACE && value <= this.MAX_SCS_ADDRESS_INTERFACE;
      
      // Verifica se è disponibile nella lista del plant
      const availableInPlant = this.availableScsAddressInterface.includes(value) ?? false;
      
      // Verifica se è usato da altre interface dello stesso riser (escludendo se stesso)
      const usedByOtherInterfaceSameRiser = currentRiserAddresses.filter((addr: number) => addr === value).length > 1;
      
      // Verifica se è usato da interface di altri riser
      const usedByOtherRisers = otherRisersAddresses.includes(value);
      
      this.addrNotValid[index] = !acceptedRange || !availableInPlant || usedByOtherInterfaceSameRiser || usedByOtherRisers;
      
      if (!acceptedRange) {
        this.invalidAddrReason[index] = this.$t('entrancePlant.riserInterfaceScsAddressOutOfRange');
      } else if (!availableInPlant) {
        this.invalidAddrReason[index] = this.$t('AppResourceEP.ScsAddressAlreadyInUse');
      } else if (usedByOtherInterfaceSameRiser || usedByOtherRisers) {
        this.invalidAddrReason[index] = this.$t('AppResourceEP.ScsAddressAlreadyInUse');
      } else {
        this.invalidAddrReason[index] = '';
      }
    });
  }

  updateInterfacesToConfigure(value: RiserInterfaceModeCombo): void {
    const currentRiserId = this.riser.riserId ?? '';
    const existingInterfaces = this.currentRiserInterfaces || [];
    const baseInterface = existingInterfaces[0];
    const newInterfaces: PeInterfaceConfig[] = [];

    switch (value) {
      case RiserInterfaceModeCombo.Mode5IndipendentRiser:
        baseInterface.mode = RiserInterfaceMode.Mode5IndipendentRiser;
        if (!this.isNewRiser) {
          baseInterface.numberOfApartments = this.numOfTotalApartments;
        }
        newInterfaces.push(baseInterface);
        break;

      case RiserInterfaceModeCombo.Mode6ExtendedRiserAndOneMode7ExtendedRiserExpansion:
        baseInterface.mode = RiserInterfaceMode.Mode6ExtendedRiser;
        if (!this.isNewRiser) {
          // Coming from 6+7+7: add the removed 3rd interface's apartments to the first
          if (this.unEditedRiserInterfaces.length > 2 && this.unEditedRiserInterfaces[2]) {
            baseInterface.numberOfApartments = (this.unEditedRiserInterfaces[0].numberOfApartments ?? 0) + (this.unEditedRiserInterfaces[2].numberOfApartments ?? 0);
          } else {
            baseInterface.numberOfApartments = this.unEditedRiserInterfaces[0].numberOfApartments;
          }
        }
        newInterfaces.push(baseInterface);
        if (this.unEditedRiserInterfaces[1]) {
          newInterfaces.push(new PeInterfaceConfig(this.unEditedRiserInterfaces[1]));
        } else {
          newInterfaces.push(this.getOrCreateExpansionInterface(existingInterfaces, currentRiserId, 1));
        }
        break;

      case RiserInterfaceModeCombo.Mode6ExtendedRiserAndTwoMode7ExtendedRiserExpansion:
        baseInterface.mode = RiserInterfaceMode.Mode6ExtendedRiser;
        if (!this.isNewRiser) {
          baseInterface.numberOfApartments = this.unEditedRiserInterfaces[0].numberOfApartments;
        }

        newInterfaces.push(baseInterface);
        if (this.unEditedRiserInterfaces[1]) {
          newInterfaces.push(new PeInterfaceConfig(this.unEditedRiserInterfaces[1]));
        } else {
          newInterfaces.push(this.getOrCreateExpansionInterface(existingInterfaces, currentRiserId, 1));
        }
        if (this.unEditedRiserInterfaces[2]) {
          newInterfaces.push(new PeInterfaceConfig(this.unEditedRiserInterfaces[2]));
        } else {
          newInterfaces.push(this.getOrCreateExpansionInterface(existingInterfaces, currentRiserId, 2));
        }
        break;
    }
    this.addrNotValid = this.addrNotValid.slice(0, newInterfaces.length);
    this.invalidAddrReason = this.invalidAddrReason.slice(0, newInterfaces.length);
    this.currentRiserInterfaces = newInterfaces;
    this.syncFloorDistributionWithInterfaces(true);
    this.validateAddresses();
  }

  private getOrCreateExpansionInterface(
    existingInterfaces: PeInterfaceConfig[],
    riserId: string,
    index: number
  ): PeInterfaceConfig {
    if (existingInterfaces.length > index) {
      const existingInterface = existingInterfaces[index];
      existingInterface.mode = RiserInterfaceMode.Mode7ExtendedRiserExpansion;
      existingInterface.gatesCount = 0;
      existingInterface.switchboardsCount = 0;
      return existingInterface;
    }

    const newInterface = new PeInterfaceConfig({
      peRiserConfigId: riserId,
      mode: RiserInterfaceMode.Mode7ExtendedRiserExpansion,
      scsAddress: Number(this.mainScsAddress) + index,
      switchboardsCount: 0,
      gatesCount: 0,
      numberOfApartments: 0,
    });
    
    return newInterface;
  }
  
}
